import React, { useEffect, useState } from "react";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarExport,
  GridActionsCellItem,
} from "@mui/x-data-grid";
import {
  Box,
  Typography,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  Tabs,
  Tab,
  CircularProgress,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";

function LinkedObjectsTabs({ changeId }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ urs_list: [], change_rules: [], functional_requirements: [] });
  const [tab, setTab] = useState(0);

  useEffect(() => {
    const fetchLinks = async () => {
      try {
        const res = await fetch(`/api/linked-objects-by-change/${changeId}`);
        const json = await res.json();
        setData(prev => ({ ...prev, urs_list: json.urs_list, change_rules: json.change_rules }));

        const frRes = await fetch(`/api/functional-requirements/by-change/${changeId}`);
        const frJson = await frRes.json();
        setData(prev => ({ ...prev, functional_requirements: frJson }));
      } catch (err) {
        console.error("Failed to load linked objects", err);
      } finally {
        setLoading(false);
      }
    };
    fetchLinks();
  }, [changeId]);

  const columnsURS = [
    { field: "id", headerName: "ID", width: 90 },
    { field: "reference_code", headerName: "Ref", width: 130 },
    { field: "title", headerName: "Title", width: 300 },
    { field: "description", headerName: "Description", width: 400 },
    { field: "acceptance_criteria", headerName: "Acceptance Criteria", width: 400 },
    { field: "impacted_modules", headerName: "Modules", width: 300 },
    { field: "status", headerName: "Status", width: 120 },
    { field: "priority", headerName: "Priority", width: 100 },
    { field: "roi_potential", headerName: "ROI", width: 100 }
  ];

  const columnsRules = [
    { field: "id", headerName: "ID", width: 70 },
    { field: "rule_type", headerName: "Type", width: 130 },
    { field: "rule_statement", headerName: "Rule", width: 400 },
    { field: "status", headerName: "Status", width: 100 },
  ];

  const columnsFR = [
    { field: "urs_reference", headerName: "URS Ref", width: 130 },
    { field: "req_number", headerName: "FR #", width: 90 },
    { field: "description", headerName: "Description", width: 500 },
    { field: "status", headerName: "Status", width: 100 },
    { field: "priority", headerName: "Priority", width: 100 },
  ];

  return (
    <Box>
      <Tabs value={tab} onChange={(_, newVal) => setTab(newVal)}>
        <Tab label="Linked URS" />
        <Tab label="Linked Rules" />
        <Tab label="Linked Requirements" />
      </Tabs>
      {loading ? (
        <CircularProgress size={24} sx={{ mt: 2 }} />
      ) : (
        <Box sx={{ height: 400, mt: 2 }}>
          {tab === 0 && (
            <DataGrid
              rows={data.urs_list || []}
              columns={columnsURS}
              getRowId={(row) => row.id}
              pageSize={5}
              rowsPerPageOptions={[5]}
            />
          )}
          {tab === 1 && (
            <DataGrid
              rows={data.change_rules || []}
              columns={columnsRules}
              getRowId={(row) => row.id}
              pageSize={5}
              rowsPerPageOptions={[5]}
            />
          )}
          {tab === 2 && (
            <DataGrid
              rows={data.functional_requirements || []}
              columns={columnsFR}
              getRowId={(row, index) => `${row.urs_reference}-${row.req_number}-${index}`}
              pageSize={5}
              rowsPerPageOptions={[5]}
            />
          )}
        </Box>
      )}
    </Box>
  );
}

function CustomToolbar() {
  return (
    <GridToolbarContainer>
      <GridToolbarExport csvOptions={{ fileName: "change_log" }} />
    </GridToolbarContainer>
  );
}

export default function ChangeLog() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedTitle, setSelectedTitle] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch("/api/change-log");
        const json = await res.json();
        setRows(json);
      } catch (err) {
        console.error("Failed to load change log", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleView = (row) => {
    setSelectedId(row.id);
    setSelectedTitle(row.title);
    setOpen(true);
  };

  const columns = [
    { field: "id", headerName: "ID", width: 70 },
    { field: "change_id", headerName: "Change ID", width: 150 },
    { field: "title", headerName: "Title", width: 300 },
    { field: "status", headerName: "Status", width: 120 },
    {
      field: "actions",
      type: "actions",
      width: 80,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<VisibilityIcon />}
          label="View"
          onClick={() => handleView(params.row)}
        />,
      ],
    },
  ];

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h5" gutterBottom>
        Change Log
      </Typography>
      <Box sx={{ height: 700 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          pageSize={10}
          rowsPerPageOptions={[10, 25]}
          components={{ Toolbar: CustomToolbar }}
          getRowId={(row) => row.id}
        />
      </Box>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Linked Objects for: {selectedTitle}</DialogTitle>
        <DialogContent>
          <LinkedObjectsTabs changeId={selectedId} />
        </DialogContent>
      </Dialog>
    </Paper>
  );
}
